<template>
  <Dialog
    v-if="data.isDialog"
    ref="overlayDialogRef"
    v-model:visible="dialogVisible"
    :header="data.description"
    :style="dialogStyle"
    :modal="false"
    :closable="true"
    :dismissable-mask="false"
    :draggable="false"
    :baseZIndex="12000"
    class="form-overlay-page-dialog"
    :content-style="dialogContentStyle"
    @show="onDialogShow"
  >
    <template #header>
      <div
        class="dialog-header"
        data-overlay-drag-handle
        draggable="false"
        @mousedown="onHeaderMouseDown"
      >
        {{ data.description }}
      </div>
    </template>
    <!-- Overlay-level actions at top -->
    <FormActionRow
      v-if="hasOverlayTopActions"
      level="overlay"
      :pageId="data.id"
      :position="['top']"
      class="overlay-actions-top"
    />
    <PanelsBlock
      v-if="panelsMounted"
      :rows="data.panelRows"
      :lazy="data.lazy"
      :page-id="overlayPageId"
    />
    <!-- Overlay-level actions at bottom -->
    <FormActionRow
      v-if="hasOverlayBottomActions"
      level="overlay"
      :pageId="data.id"
      :position="['bottom', 'right']"
      class="overlay-actions-bottom"
    />
  </Dialog>

  <Teleport v-else :to="teleportTarget" :disabled="!teleportTarget">
    <div
      v-show="visible"
      ref="overlayRef"
      class="form-overlay-page"
      :class="{ 'form-overlay-page--single-pgroup': hideOverlayHandle }"
      :style="overlayStyle"
    >
      <div
        v-if="!hideOverlayHandle"
        class="form-overlay-page__handle"
        data-overlay-drag-handle
      >
        <span class="form-overlay-page__title">{{ data.description }}</span>
      </div>
      <!-- Overlay-level actions at top (before panels) -->
      <FormActionRow
        v-if="hasOverlayTopActions"
        level="overlay"
        :pageId="data.id"
        :position="['top']"
        class="overlay-actions-top"
      />
      <div class="form-overlay-page__content">
        <PanelsBlock
          v-if="panelsMounted"
          :rows="data.panelRows"
          :lazy="data.lazy"
          :page-id="overlayPageId"
        />
      </div>
      <!-- Overlay-level actions at bottom (after panels) -->
      <FormActionRow
        v-if="hasOverlayBottomActions"
        level="overlay"
        :pageId="data.id"
        :position="['bottom', 'right']"
        class="overlay-actions-bottom"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useFormOverlayPage } from '@/common/composables/useFormOverlayPage'
import { usePlatformDialogDrag } from '@/common/composables/usePlatformDialogDrag'
import { isSinglePGroupOverlay } from '@/common/utils/formOverlayPanelRows'
import { resolveFormOverlayInitialVisible } from '@/common/utils/overlayPendingVisibility'
import PanelsBlock from '@/components/platform/panel/Block.vue'
import FormActionRow from '@/components/platform/form/FormActionRow.vue'
import { usePageData } from '@/common/composables/usePageData'
import {
  RegisterOverlayKey,
  UnRegisterOverlayKey,
  FormOverlayHostKey,
  FormOverlayLayerKey,
  FormOverlayRemeasureKey,
  FormProviderKey,
} from '@/components/platform/injection-keys'
import type { FormOverlay } from '@/services/parse/types'
import Dialog from 'primevue/dialog'
import { computed, inject, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'

interface Props {
  data: FormOverlay
  formId: string
}

const props = defineProps<Props>()

const { pageActions } = usePageData()

const overlayRef = ref<HTMLElement | null>(null)
const overlayDialogRef = ref<{ $el?: HTMLElement } | null>(null)
const overlayDialogEl = ref<HTMLElement | null>(null)
const hostElRef = inject(FormOverlayHostKey, ref<HTMLElement | null>(null))
const layerElRef = inject(FormOverlayLayerKey, ref<HTMLElement | null>(null))
const panelsMounted = ref(!props.data.lazy)
const lazyReloadDone = ref(false)
const dialogOnClickRan = ref(false)

const registerOverlay = inject(RegisterOverlayKey)
const unregisterOverlay = inject(UnRegisterOverlayKey)
const formProvider = inject(FormProviderKey, null)

const overlayPageId = computed(() => `overlay-${props.data.id}`)
const hideOverlayHandle = computed(() => isSinglePGroupOverlay(props.data.panelRows))

const hasOverlayTopActions = computed(() => {
  const overlayId = props.data.id.toString()
  return pageActions.value.some((action: any) =>
    action.layout?.level?._text === 'overlay' &&
    action.layout?.id?._text === overlayId &&
    action.layout?.position?._text === 'top'
  )
})

const hasOverlayBottomActions = computed(() => {
  const overlayId = props.data.id.toString()
  return pageActions.value.some((action: any) =>
    action.layout?.level?._text === 'overlay' &&
    action.layout?.id?._text === overlayId &&
    (action.layout?.position?._text === 'bottom' || action.layout?.position?._text === 'right')
  )
})

const dialogContentStyle = {
  overflow: 'visible',
  maxHeight: '85vh',
}
const dialogSizeStyle = computed(() => ({
  width: props.data.width > 0 ? `${props.data.width}px` : '800px',
  maxWidth: '95vw',
}))

const resolveOverlayDialogEl = (): HTMLElement | null => {
  const componentRoot = overlayDialogRef.value?.$el
  if (componentRoot instanceof HTMLElement) {
    if (componentRoot.classList.contains('p-dialog')) {
      return componentRoot
    }
    const nested = componentRoot.querySelector<HTMLElement>('.p-dialog')
    if (nested) {
      return nested
    }
  }
  return document.querySelector<HTMLElement>('.p-dialog.form-overlay-page-dialog')
}

const refreshOverlayDialogEl = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      overlayDialogEl.value = resolveOverlayDialogEl()
    })
  })
}

const onDialogShow = async () => {
  refreshOverlayDialogEl()
  await ensureOverlayPanelsLoaded()
  await runOverlayOnClickIfNeeded()
  formProvider?.flushPendingOverlayRefills?.()
  refreshOverlayDialogEl()
}

const ensureOverlayPanelsLoaded = async () => {
  if (!panelsMounted.value) {
    panelsMounted.value = true
    await nextTick()
  }
  if (!props.data.lazy || lazyReloadDone.value) return

  const pageId = Number(props.data.id)
  if (!Number.isFinite(pageId) || !formProvider?.reloadLazyPage) return

  lazyReloadDone.value = true
  try {
    await formProvider.reloadLazyPage(pageId, props.formId)
  } catch {
    /* skip */
  }
}

const runOverlayOnClickIfNeeded = async () => {
  const code = props.data.onClick?.trim()
  if (!code || dialogOnClickRan.value) return
  dialogOnClickRan.value = true
  try {
    await formProvider?.runCode?.(code)
  } catch (error) {
    console.warn('[FormOverlayPage] overlay onClick failed:', error)
  }
}

const {
  visible,
  overlayStyle,
  setVisible,
  isVisible,
  applyInitialBounds,
  remeasureBounds,
  effectiveLayerEl,
} = useFormOverlayPage({
  anchorEl: hostElRef,
  layerEl: layerElRef,
  overlayEl: overlayRef,
  defBounds: {
    posX: props.data.posX,
    posY: props.data.posY,
    width: props.data.width,
    height: props.data.height,
  },
  initialVisible: resolveFormOverlayInitialVisible(props.data.isDialog),
})

const teleportTarget = computed(() => effectiveLayerEl.value ?? undefined)

provide(FormOverlayRemeasureKey, (_expanded: boolean) => {
  requestAnimationFrame(() => remeasureBounds())
})

const dialogVisible = computed({
  get: () => visible.value,
  set: (value: boolean) => setVisible(value),
})

const { onHeaderMouseDown } = usePlatformDialogDrag()

const dialogStyle = computed(() => ({
  ...dialogSizeStyle.value,
}))

watch(dialogVisible, (isOpen) => {
  if (!props.data.isDialog) return
  if (!isOpen) {
    overlayDialogEl.value = null
    return
  }
  refreshOverlayDialogEl()
}, { immediate: true })

watch(visible, async (isVisible) => {
  if (!isVisible || props.data.isDialog) return
  await ensureOverlayPanelsLoaded()
  applyInitialBounds()
  formProvider?.flushPendingOverlayRefills?.()
})

onMounted(async () => {
  registerOverlay?.({
    name: props.data.name,
    overlayId: props.data.id,
    formId: props.formId,
    setVisible,
    isVisible,
    isDialog: props.data.isDialog,
  })
  if (!visible.value || props.data.isDialog) return
  await ensureOverlayPanelsLoaded()
  applyInitialBounds()
  formProvider?.flushPendingOverlayRefills?.()
})

onUnmounted(() => {
  unregisterOverlay?.({
    name: props.data.name,
    formId: props.formId,
  })
})
</script>

<style scoped lang="scss">
.form-overlay-page {
  position: absolute;
  z-index: 200;
  display: flex;
  flex-direction: column;
  min-width: 200px;
  min-height: 27px;
  background: var(--p-content-background, #fff);
  border: 1px solid var(--p-content-border-color, #ccc);
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  pointer-events: auto;
}

.form-overlay-page--single-pgroup {
  border: none;
  box-shadow: none;
  background: transparent;
  min-width: 0;
}

.form-overlay-page__handle {
  cursor: move;
  user-select: none;
  padding: 0.35rem 0.75rem;
  background: var(--p-surface-100, #f4f4f5);
  border-bottom: 1px solid var(--p-content-border-color, #ddd);
  touch-action: none;
}

.form-overlay-page__title {
  font-size: 0.875rem;
  font-weight: 600;
}

.form-overlay-page__content {
  flex: 1 1 auto;
  overflow: auto;
  padding: 0.5rem;
}

.form-overlay-page--single-pgroup .form-overlay-page__content {
  padding: 0;
  overflow: visible;
}

.overlay-actions-top,
.overlay-actions-bottom {
  padding: 0.5rem 0.75rem;
}

.overlay-actions-top {
  border-bottom: 1px solid var(--p-content-border-color, #ddd);
}

.overlay-actions-bottom {
  border-top: 1px solid var(--p-content-border-color, #ddd);
  margin-top: auto;
}
</style>

<style lang="scss">
.form-overlay-page-dialog {
  .p-dialog-header {
    cursor: move;
    user-select: none;
    touch-action: none;
    padding: 1rem 1.25rem 0.75rem;
  }

  .dialog-header {
    cursor: move;
    user-select: none;
    touch-action: none;
  }

  .p-dialog-header-actions {
    cursor: default;
    user-select: auto;
    touch-action: auto;
  }

  .p-dialog-content {
    padding: 1.25rem 2.25rem 1rem;
  }

  .panels-flex {
    width: 100%;
  }

  .m-col {
    overflow: visible !important;
  }

  .panel-content-main {
    width: 100%;
  }

  .panel-content-main .elements-row {
    justify-content: center;
  }

  .panel-content-main .element-container {
    flex: 0 1 auto !important;
    max-width: 100% !important;
    min-width: 0;
  }

  .panel-content-main .element-container.element-type-label {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  .panel-content-main .element-content-wrapper,
  .panel-content-main .element-content {
    width: 100%;
  }

  .panel-content-main .element-simple-label--align-center {
    justify-content: center;
    text-align: center;
  }

  .panel-content-main .element-simple-label--align-left {
    justify-content: flex-start;
    text-align: left;
  }

  .panel-content-main .element-simple-label--align-right {
    justify-content: flex-end;
    text-align: right;
  }

  .panel-content-main .element-simple-label {
    font-weight: 700;
  }

  .panel-content-main .element-simple-label span[style*='background-color: blue'] {
    display: block;
    width: 335px;
    max-width: min(335px, 70vw);
    height: 22px;
    overflow: hidden;
    color: transparent !important;
    background-color: blue;
    line-height: 22px;
  }

  .panel-actions-bottom,
  .panel-actions-bottom .element-actions {
    justify-content: center;
  }

  .panel-actions-bottom {
    margin-top: 0.5rem;
  }

  .element-actions {
    min-height: 2.25rem;
  }

  .overlay-actions-top {
    padding: 0.75rem 2.25rem 0.5rem;
  }

  .overlay-actions-bottom {
    padding: 0.5rem 2.25rem 0.75rem;
    margin-top: 0.5rem;
    display: flex;
    justify-content: flex-end;
  }
}

html.platform-dialog-dragging,
html.platform-dialog-dragging * {
  user-select: none !important;
}

html.platform-dialog-dragging .p-dialog,
html.platform-dialog-dragging .p-dialog-mask {
  transition: none !important;
}
</style>
